import React, { useContext, useState, useRef } from "react";
import ConversationsSidebarContainer from "@/components/chat/ConversationsSidebarContainer";
import ChatAreaChild from "@/components/chat/ChatAreaChild";
import { useGetChatRoomsQuery } from "@/api/chatApi/chat";
import { ChatContext, ChatContextInterface } from "@/components/MSRContext";
import Global from "@/globals/globals";
import type { ChatRoom } from "@/types/chat.types";
import generateRandomString from "../MSRP/utilities/generators";
import { createEmptyMultiPart, MessagePart } from "../utils/multiPartBuilder";
import { MyRequest } from "../models/MyRequest";
import Navbar from "../components/navbar";
import CompactSidebar from "../components/ResponsiveCompactSidebar";
import { SidebarProvider } from "../context/SidebarContext";

/**
 * ChatDashboard - New unified chat dashboard
 *
 * This page shows the chat list (sidebar) and chat area side-by-side
 * using parent-child architecture with NO URL navigation.
 *
 * Architecture:
 * ChatDashboard (Page)
 * └── ConversationsSidebarContainer (PARENT)
 *     ├── Sidebar with room list (always visible)
 *     └── ChatAreaChild (CHILD - shows selected room)
 */

const ChatDashboard: React.FC = () => {
  // Get MSRP wrapper, context functions, and rooms from WebSocket
  const { wrapper, handleaddServerRequest, rooms: contextRooms } = useContext(ChatContext) as ChatContextInterface;

  // Track selected room for MSRP operations
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);

  // Ref to store the clear selection function from ConversationsSidebarContainer
  const clearSelectionRef = useRef<(() => void) | null>(null);

  // Ref to store the select function from ConversationsSidebarContainer
  const selectRoomRef = useRef<((room: ChatRoom) => void) | null>(null);

  // Fetch rooms via RTK Query
  const { data, isLoading, refetch } = useGetChatRoomsQuery({
    personal: true,
    "chat-type": "any",
    "session-status": "both",
  });

  const rooms = data?.chatRooms || [];

  // Helper function to get receiver username from participants for agent-to-agent
  const getReceiverUsername = (room: ChatRoom): string => {
    if (room.session_type === "agent-to-agent") {
      const receiver = room.participants?.find(p => p.is_current_user === false);
      if (receiver?.user_type === 3 && receiver.details) {
        return (receiver.details as any).agent_username + Global.companyDomain;
      }
    }
    return "";
  };

  // Refetch rooms when new room is added via WebSocket
  React.useEffect(() => {
    if (contextRooms && contextRooms.length > 0) {
      console.log("📡 WebSocket rooms updated, refetching from REST API...");
      refetch();
    }
  }, [contextRooms.length, refetch]);

  // Delete message using MSRP protocol
  const handleDeleteMessage = (messageId: string, agentUsername?: string) => {
    if (!selectedRoom) return;

    const id = generateRandomString(16);
    const toName = selectedRoom.session_type_id === 3
      ? selectedRoom.session_key
      : selectedRoom.session_type === "c2a"
        ? selectedRoom?.client?.client_username + Global.companyDomain || ""
        : selectedRoom.session_type === "agent-to-agent"
        ? getReceiverUsername(selectedRoom) // For agent-to-agent, get receiver from participants
        : selectedRoom?.agent?.agent_username + Global.companyDomain || "";

    const payload = {
      id,
      type: "request",
      job: "delete-message",
      parameters: {
        "deleted-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "delete-time": Math.floor(Date.now() / 1000),
      },
    };

    wrapper?.sendMsg(JSON.stringify(payload, null, 4), "application/rechat.cntl+json");

    handleaddServerRequest?.(
      new MyRequest(
        id,
        Global.username + Global.companyDomain,
        toName,
        "delete-message",
        "pending",
        "This message has been deleted",
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  // Edit message using MSRP protocol
  const handleEditMessage = (messageId: string, newText: string) => {
    if (!selectedRoom) return;

    const toName =
      selectedRoom.session_type === "group-chat"
        ? selectedRoom.session_key
        : selectedRoom.session_type === "c2a"
          ? selectedRoom?.client?.client_username + Global.companyDomain || ""
          : selectedRoom.session_type === "agent-to-agent"
          ? getReceiverUsername(selectedRoom) // For agent-to-agent, get receiver from participants
          : selectedRoom?.agent?.agent_username + Global.companyDomain || "";

    const multipartMessage = createEmptyMultiPart();
    const reqID = generateRandomString(16);

    const messageJson = {
      id: reqID,
      type: "request",
      job: "edit-message",
      parameters: {
        "current-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "chat-type": selectedRoom.session_type,
      },
    };

    const messageJsonString = JSON.stringify(messageJson, null, 4);

    const part1 = new MessagePart({
      name: "part1",
      contentType: "application/rechat.cntl+json\r\n",
      filename: "file1.txt",
      value: messageJsonString,
      contentDisposition: "attachment",
    });

    const part2 = new MessagePart({
      name: "part2",
      contentType: "text/plain\r\n",
      filename: "file2.txt",
      value: newText,
      contentDisposition: "attachment",
    });

    multipartMessage.addPart(part1);
    multipartMessage.addPart(part2);

    const Body = multipartMessage.toString();
    const Boundary = multipartMessage.boundary;
    const Type = 'multipart/related;boundary="' + Boundary + '"';

    wrapper?.sendMsg(Body, Type);

    handleaddServerRequest?.(
      new MyRequest(
        reqID,
        Global.username + Global.companyDomain,
        selectedRoom.agent?.agent_username,
        "edit-message",
        "pending",
        newText,
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  // Send message - handled by MessageInput component
  const handleSendMessage = (text: string, sender: string, isOwner: boolean, id: string) => {
    console.log("Message sent via MessageInput:", text);
  };

  // Send file message
  const handleSendFileMessage = (fileMessage: any) => {
    console.log("File message sent:", fileMessage.fileName);
  };

  const handleRoomSelect = (room: ChatRoom) => {
    console.log("✅ Room selected via PROPS (no URL change):", room.session_key);
    setSelectedRoom(room);
  };

  const handleRegisterClearFunction = (clearFn: () => void) => {
    console.log("📝 Registered clear selection function from ConversationsSidebarContainer");
    clearSelectionRef.current = clearFn;
  };

  const handleRegisterSelectFunction = (selectFn: (room: ChatRoom) => void) => {
    console.log("📝 Registered select function from ConversationsSidebarContainer");
    selectRoomRef.current = selectFn;
  };

  const handleClearSelection = () => {
    console.log("🔄 Clearing selected room in ChatDashboard");
    setSelectedRoom(null);

    // Also clear the selection in ConversationsSidebarContainer
    if (clearSelectionRef.current) {
      clearSelectionRef.current();
    }
  };

  const handleAutoSelectRoom = (sessionKey: string) => {
    console.log("🎯 Auto-selecting room with session_key:", sessionKey);

    // Find the room in the rooms list
    const roomToSelect = rooms.find(r => r.session_key === sessionKey);

    if (roomToSelect) {
      console.log("✅ Found room to auto-select:", roomToSelect.session_key);

      // Update ChatDashboard's state
      setSelectedRoom(roomToSelect);

      // Also update ConversationsSidebarContainer's state
      if (selectRoomRef.current) {
        selectRoomRef.current(roomToSelect);
      }
    } else {
      console.warn("⚠️ Room not found for auto-selection:", sessionKey);
      // Refetch rooms and try again after a short delay
      console.log("🔄 Refetching rooms and retrying...");
      refetch().then(() => {
        setTimeout(() => {
          const retryRoom = rooms.find(r => r.session_key === sessionKey);
          if (retryRoom) {
            console.log("✅ Found room on retry:", retryRoom.session_key);

            // Update ChatDashboard's state
            setSelectedRoom(retryRoom);

            // Also update ConversationsSidebarContainer's state
            if (selectRoomRef.current) {
              selectRoomRef.current(retryRoom);
            }
          } else {
            console.error("❌ Room still not found after refetch:", sessionKey);
          }
        }, 300);
      });
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <SidebarProvider>
        <Navbar />
        <div className="flex items-start pt-16">
          <CompactSidebar />

          {/* Main content area with sidebar + chat */}
          <div className="flex-1 flex h-[calc(100vh-4rem)] w-full">
            <ConversationsSidebarContainer
              rooms={rooms}
              isLoading={isLoading}
              onRoomSelect={handleRoomSelect}
              onClearSelection={handleRegisterClearFunction}
              onRegisterSelectFn={handleRegisterSelectFunction}
            >
              {(selectedRoom) => (
                <ChatAreaChild
                  room={selectedRoom}
                  wrapper={wrapper as any}
                  currentUser={Global.username + Global.companyDomain}
                  onEditMessage={handleEditMessage}
                  onDeleteMessage={handleDeleteMessage}
                  onSendMessage={handleSendMessage}
                  onSendFileMessage={handleSendFileMessage}
                  onRefreshRooms={refetch}
                  onClearSelection={handleClearSelection}
                  onAutoSelectRoom={handleAutoSelectRoom}
                />
              )}
            </ConversationsSidebarContainer>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default ChatDashboard;
